import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import adminApiService from '../services/api';
import {
  DashboardQueues,
  AcquisitionMetrics,
  ChatMetrics,
  ResourceMetrics,
} from '../types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function DashboardPage() {
  const [queues, setQueues] = useState<DashboardQueues | null>(null);
  const [acquisition, setAcquisition] = useState<AcquisitionMetrics | null>(null);
  const [chats, setChats] = useState<ChatMetrics | null>(null);
  const [resources, setResources] = useState<ResourceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollInterval, setPollInterval] = useState(30000); // 30 seconds default

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const [queuesData, acquisitionData, chatsData, resourcesData] = await Promise.all([
        adminApiService.getQueueMetrics(),
        adminApiService.getAcquisitionMetrics(),
        adminApiService.getChatMetrics(),
        adminApiService.getResourceMetrics(),
      ]);

      setQueues(queuesData);
      setAcquisition(acquisitionData);
      setChats(chatsData);
      setResources(resourcesData);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Set up polling
    const interval = setInterval(fetchDashboardData, pollInterval);
    return () => clearInterval(interval);
  }, [pollInterval]);

  if (loading && !queues) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">Error: {error}</div>
        <button
          onClick={fetchDashboardData}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex items-center space-x-4">
          <label className="text-sm text-gray-600">
            Auto-refresh:
            <select
              value={pollInterval}
              onChange={(e) => setPollInterval(Number(e.target.value))}
              className="ml-2 border border-gray-300 rounded px-2 py-1"
            >
              <option value={10000}>10 seconds</option>
              <option value={30000}>30 seconds</option>
              <option value={60000}>1 minute</option>
              <option value={300000}>5 minutes</option>
              <option value={0}>Off</option>
            </select>
          </label>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Queue Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <QueueCard
          title="Claude Processing"
          value={queues?.claude.processing || 0}
          subtitle="Currently processing"
        />
        <QueueCard
          title="Claude Pending"
          value={queues?.claude.pending || 0}
          subtitle="Waiting in queue"
        />
        <QueueCard
          title="Inference Processing"
          value={queues?.inference.processing || 0}
          subtitle="Currently processing"
        />
        <QueueCard
          title="Inference Pending"
          value={queues?.inference.pending || 0}
          subtitle="Waiting in queue"
        />
      </div>

      {/* Queue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QueueChart
          title="Claude Jobs"
          completed={queues?.claude.completedPerHour || []}
          failed={queues?.claude.failedPerHour || []}
        />
        <QueueChart
          title="Inference Jobs"
          completed={queues?.inference.completedPerHour || []}
          failed={queues?.inference.failedPerHour || []}
        />
      </div>

      {/* Acquisition Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Acquisition Metrics</h2>
        <AcquisitionChart data={acquisition} />
      </div>

      {/* Chat Metrics */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Chat Metrics</h2>
        <ChatChart data={chats} />
      </div>

      {/* Resource Metrics */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Resource Usage</h2>
        <ResourceCharts data={resources} />
      </div>
    </div>
  );
}

function QueueCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: number;
  subtitle: string;
}) {
  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-sm font-medium text-gray-600">{title}</h3>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
      <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
    </div>
  );
}

function QueueChart({
  title,
  completed,
  failed,
}: {
  title: string;
  completed: Array<{ hour: string; count: number }>;
  failed: Array<{ hour: string; count: number }>;
}) {
  const labels = completed.map((item) => new Date(item.hour).toLocaleString());
  const completedData = completed.map((item) => item.count);
  const failedData = failed.map((item) => item.count);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Completed',
        data: completedData,
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Failed',
        data: failedData,
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: title,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <Line data={chartData} options={options} />
    </div>
  );
}

function AcquisitionChart({ data }: { data: AcquisitionMetrics | null }) {
  if (!data) return <div>No data available</div>;

  const labels = data.signupsPerDay.map((item) => item.date);
  const signupsData = data.signupsPerDay.map((item) => item.count);
  const conversionsData = data.conversionsPerDay.map((item) => item.count);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Signups',
        data: signupsData,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Conversions',
        data: conversionsData,
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

function ChatChart({ data }: { data: ChatMetrics | null }) {
  if (!data) return <div>No data available</div>;

  const labels = data.chatsPerDay.map((item) => item.date);
  const chatsData = data.chatsPerDay.map((item) => item.count);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Chats per Day',
        data: chatsData,
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

function ResourceCharts({ data }: { data: ResourceMetrics | null }) {
  if (!data) return <div>No data available</div>;

  return (
    <div className="space-y-6">
      {/* Token Usage */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Token Usage</h3>
        <TokenUsageChart data={data.tokensPerDay} />
      </div>

      {/* AWS Usage */}
      <div>
        <h3 className="text-lg font-semibold mb-4">AWS Usage</h3>
        <AWSUsageChart data={data.awsPerDay} />
      </div>

      {/* Hugging Face Usage */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Hugging Face Jobs</h3>
        <HFUsageChart data={data.huggingFacePerDay} />
      </div>
    </div>
  );
}

function TokenUsageChart({
  data,
}: {
  data: ResourceMetrics['tokensPerDay'];
}) {
  const labels = data.input.map((item) => item.date);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Input Tokens',
        data: data.input.map((item) => item.count),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Output Tokens',
        data: data.output.map((item) => item.count),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Cache Read',
        data: data.cacheRead.map((item) => item.count),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Cache Write',
        data: data.cacheWrite.map((item) => item.count),
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

function AWSUsageChart({ data }: { data: ResourceMetrics['awsPerDay'] }) {
  const labels = data.transcription.map((item) => item.date);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Transcription (minutes)',
        data: data.transcription.map((item) => item.minutes),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Rekognition (minutes)',
        data: data.rekognition.map((item) => item.minutes),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
      },
      {
        label: 'SageMaker (seconds)',
        data: data.sagemaker.map((item) => item.seconds),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

function HFUsageChart({
  data,
}: {
  data: Array<{ date: string; count: number }>;
}) {
  const labels = data.map((item) => item.date);
  const counts = data.map((item) => item.count);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Hugging Face Jobs',
        data: counts,
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

